/**
 * ReportsPage Component
 * Displays compliance dashboard, analytics, and reports
 */

import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Toast } from './shared/Toast.js';

interface ComplianceSummary {
  total_extinguishers: number;
  compliant: number;
  warning: number;
  overdue: number;
  last_updated: string;
}

interface OverdueExtinguisher {
  id: string;
  qr_code: string;
  location_name: string;
  days_overdue: number;
  last_inspection_date: string | null;
  extinguisher_type: string;
}

export class ReportsPage extends BaseComponent {
  private isLoading: boolean = true;
  private summary: ComplianceSummary | null = null;
  private overdueList: OverdueExtinguisher[] = [];
  private recentInspections: any[] = [];

  async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
    // Replace the loading state instead of stacking a second copy of the page.
    this.shadow.replaceChildren();
    await this.render();
  }

  private async loadData(): Promise<void> {
    try {
      // Load compliance summary
      const summaryResponse = await api.get('/api/compliance/summary');
      this.summary = summaryResponse;

      // Load overdue extinguishers
      try {
        const overdueResponse = await api.get('/api/compliance/overdue');
        this.overdueList = overdueResponse.overdue || [];
      } catch (error) {
        console.error('Failed to load overdue list:', error);
        this.overdueList = [];
      }

      // Load recent inspections
      try {
        const inspectionsResponse = await api.get('/api/inspections?limit=10');
        this.recentInspections = inspectionsResponse.inspections || [];
      } catch (error) {
        console.error('Failed to load recent inspections:', error);
        this.recentInspections = [];
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Failed to load reports data:', error);
      this.isLoading = false;
    }
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'page-container' });

    // Header with navigation
    const header = this.createHeader();
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'reports-main' });

    if (this.isLoading) {
      main.appendChild(this.createElement('loading-spinner'));
    } else {
      // Page title
      const pageTitle = this.createElement('div', { className: 'page-title' });
      pageTitle.appendChild(this.createElement('h1', { textContent: 'Compliance Reports & Analytics' }));
      pageTitle.appendChild(this.createElement('p', {
        className: 'subtitle',
        textContent: 'Overview of fire extinguisher compliance status across all locations'
      }));
      main.appendChild(pageTitle);

      // Summary cards
      const summarySection = this.createSummaryCards();
      main.appendChild(summarySection);

      // Overdue extinguishers
      if (this.overdueList.length > 0) {
        const overdueSection = this.createOverdueSection();
        main.appendChild(overdueSection);
      }

      // Recent inspections
      const recentSection = this.createRecentInspectionsSection();
      main.appendChild(recentSection);

      // PDF Reports section
      const pdfReportsSection = this.createPDFReportsSection();
      main.appendChild(pdfReportsSection);

      // Export button
      const exportButton = this.createExportButton();
      main.appendChild(exportButton);
    }

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createHeader(): HTMLElement {
    const header = this.createElement('header', { className: 'page-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', {
      className: 'logo',
      textContent: 'FireGuard'
    });
    logo.style.cursor = 'pointer';
    logo.addEventListener('click', () => router.push('/dashboard'));

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/reports', () => this.handleLogout());
    headerContent.appendChild(nav);
    header.appendChild(headerContent);

    return header;
  }

  private createSummaryCards(): HTMLElement {
    const section = this.createElement('section', { className: 'summary-section' });

    if (!this.summary) {
      return section;
    }

    const cards = [
      {
        title: 'Total Extinguishers',
        value: this.summary.total_extinguishers,
        icon: '🧯',
        color: 'blue'
      },
      {
        title: 'Compliant',
        value: this.summary.compliant,
        icon: '✅',
        color: 'green',
        percentage: this.summary.total_extinguishers > 0
          ? Math.round((this.summary.compliant / this.summary.total_extinguishers) * 100)
          : 0
      },
      {
        title: 'Warning',
        value: this.summary.warning,
        icon: '⚠️',
        color: 'yellow',
        percentage: this.summary.total_extinguishers > 0
          ? Math.round((this.summary.warning / this.summary.total_extinguishers) * 100)
          : 0
      },
      {
        title: 'Overdue',
        value: this.summary.overdue,
        icon: '❌',
        color: 'red',
        percentage: this.summary.total_extinguishers > 0
          ? Math.round((this.summary.overdue / this.summary.total_extinguishers) * 100)
          : 0
      }
    ];

    const cardsGrid = this.createElement('div', { className: 'cards-grid' });

    cards.forEach(card => {
      const cardEl = this.createElement('div', { className: `summary-card card-${card.color}` });

      const icon = this.createElement('div', {
        className: 'card-icon',
        textContent: card.icon
      });

      const content = this.createElement('div', { className: 'card-content' });
      const title = this.createElement('div', {
        className: 'card-title',
        textContent: card.title
      });
      const value = this.createElement('div', {
        className: 'card-value',
        textContent: card.value.toString()
      });

      content.appendChild(title);
      content.appendChild(value);

      if (card.percentage !== undefined) {
        const percentage = this.createElement('div', {
          className: 'card-percentage',
          textContent: `${card.percentage}%`
        });
        content.appendChild(percentage);
      }

      cardEl.appendChild(icon);
      cardEl.appendChild(content);
      cardsGrid.appendChild(cardEl);
    });

    section.appendChild(cardsGrid);

    // Last updated timestamp
    if (this.summary.last_updated) {
      const lastUpdated = this.createElement('p', {
        className: 'last-updated',
        textContent: `Last updated: ${new Date(this.summary.last_updated).toLocaleString()}`
      });
      section.appendChild(lastUpdated);
    }

    return section;
  }

  private createOverdueSection(): HTMLElement {
    const section = this.createElement('section', { className: 'overdue-section' });

    const header = this.createElement('div', { className: 'section-header' });
    header.appendChild(this.createElement('h2', {
      textContent: `⚠️ Overdue Inspections (${this.overdueList.length})`
    }));
    header.appendChild(this.createElement('p', {
      className: 'section-subtitle',
      textContent: 'These extinguishers require immediate attention'
    }));
    section.appendChild(header);

    const table = this.createElement('div', { className: 'overdue-table' });

    // Table header
    const tableHeader = this.createElement('div', { className: 'table-row table-header' });
    ['QR Code', 'Location', 'Type', 'Days Overdue', 'Last Inspection', 'Action'].forEach(heading => {
      tableHeader.appendChild(this.createElement('div', {
        className: 'table-cell',
        textContent: heading
      }));
    });
    table.appendChild(tableHeader);

    // Table rows
    this.overdueList.forEach(item => {
      const row = this.createElement('div', { className: 'table-row' });

      row.appendChild(this.createElement('div', {
        className: 'table-cell',
        textContent: item.qr_code
      }));

      row.appendChild(this.createElement('div', {
        className: 'table-cell',
        textContent: item.location_name || 'Unknown'
      }));

      row.appendChild(this.createElement('div', {
        className: 'table-cell',
        textContent: item.extinguisher_type
      }));

      const daysCell = this.createElement('div', { className: 'table-cell' });
      const daysBadge = this.createElement('span', {
        className: 'days-badge',
        textContent: `${item.days_overdue} days`
      });
      daysCell.appendChild(daysBadge);
      row.appendChild(daysCell);

      row.appendChild(this.createElement('div', {
        className: 'table-cell',
        textContent: item.last_inspection_date
          ? formatDate(item.last_inspection_date)
          : 'Never'
      }));

      const actionCell = this.createElement('div', { className: 'table-cell' });
      const inspectBtn = this.createElement('button', {
        className: 'btn-small btn-primary',
        textContent: 'Inspect Now'
      });
      inspectBtn.addEventListener('click', () => {
        router.push(`/extinguishers/${item.id}/inspect`);
      });
      actionCell.appendChild(inspectBtn);
      row.appendChild(actionCell);

      table.appendChild(row);
    });

    section.appendChild(table);
    return section;
  }

  private createRecentInspectionsSection(): HTMLElement {
    const section = this.createElement('section', { className: 'recent-section' });

    const header = this.createElement('h2', {
      textContent: `Recent Inspections (${this.recentInspections.length})`
    });
    section.appendChild(header);

    if (this.recentInspections.length === 0) {
      section.appendChild(this.createElement('p', {
        className: 'empty-message',
        textContent: 'No inspections recorded yet'
      }));
      return section;
    }

    const list = this.createElement('div', { className: 'inspections-list' });

    this.recentInspections.forEach(inspection => {
      const item = this.createElement('div', { className: 'inspection-item' });

      const statusIcon = inspection.status === 'pass' ? '✅' :
                         inspection.status === 'warning' ? '⚠️' : '❌';

      const iconDiv = this.createElement('div', {
        className: 'inspection-icon',
        textContent: statusIcon
      });

      const content = this.createElement('div', { className: 'inspection-content' });

      const title = this.createElement('div', {
        className: 'inspection-title',
        textContent: `${inspection.extinguisher?.qr_code || 'Unknown'} - ${inspection.inspection_type.toUpperCase()}`
      });

      const meta = this.createElement('div', { className: 'inspection-meta' });
      const date = new Date(inspection.inspection_date).toLocaleString();
      const inspector = inspection.inspector_name || 'Unknown';
      meta.textContent = `${date} • Inspector: ${inspector}`;

      const status = this.createElement('div', {
        className: `inspection-status status-${inspection.status}`,
        textContent: inspection.status.toUpperCase()
      });

      content.appendChild(title);
      content.appendChild(meta);

      item.appendChild(iconDiv);
      item.appendChild(content);
      item.appendChild(status);

      list.appendChild(item);
    });

    section.appendChild(list);
    return section;
  }

  private createPDFReportsSection(): HTMLElement {
    const section = this.createElement('section', { className: 'pdf-reports-section' });

    const header = this.createElement('h2', {
      textContent: '📄 PDF Reports'
    });
    section.appendChild(header);

    const description = this.createElement('p', {
      className: 'section-subtitle',
      textContent: 'Generate professional PDF reports for compliance tracking and audits'
    });
    section.appendChild(description);

    const reportsGrid = this.createElement('div', { className: 'pdf-reports-grid' });

    // Nothing in the account means nothing to report. Saying so is better than
    // three buttons that each return an error.
    // Only suppress the PDF cards once we know the account is genuinely empty.
    // A null summary means "still loading", not "nothing here".
    const nothingToReport = this.summary !== null && (this.summary.total_extinguishers ?? 0) === 0;
    if (nothingToReport) {
      const note = this.createElement('div', { className: 'pdf-empty-note' });
      note.appendChild(this.createElement('h3', {}, 'Nothing to report yet'));
      note.appendChild(
        this.createElement(
          'p',
          {},
          'Reports are built from your extinguishers and their inspection history. Add a location and your extinguishers first, and these become the documents you hand an inspector.'
        )
      );
      const go = this.createElement('button', { className: 'btn-primary', type: 'button' }, 'Add your extinguishers');
      go.addEventListener('click', () => router.navigate('/extinguishers'));
      note.appendChild(go);
      section.appendChild(note);
    }

    if (!nothingToReport) {
      // Compliance Summary Report
      const complianceCard = this.createElement('div', { className: 'pdf-report-card' });
      complianceCard.innerHTML = `
        <div class="pdf-report-icon">✓</div>
        <h3>Compliance Summary</h3>
        <p>Overview of compliance status across all locations</p>
      `;
      const complianceBtn = this.createElement('button', {
        className: 'btn-primary btn-block',
        textContent: 'Generate PDF'
      });
      complianceBtn.addEventListener('click', () => this.downloadPDFReport('compliance'));
      complianceCard.appendChild(complianceBtn);
      reportsGrid.appendChild(complianceCard);

      // Overdue Report
      const overdueCard = this.createElement('div', { className: 'pdf-report-card' });
      overdueCard.innerHTML = `
        <div class="pdf-report-icon">⚠️</div>
        <h3>Overdue Inspections</h3>
        <p>List of extinguishers requiring immediate attention</p>
      `;
      const overdueBtn = this.createElement('button', {
        className: 'btn-primary btn-block',
        textContent: 'Generate PDF'
      });
      overdueBtn.addEventListener('click', () => this.downloadPDFReport('overdue'));
      overdueCard.appendChild(overdueBtn);
      reportsGrid.appendChild(overdueCard);

      // Annual Audit Report
      const auditCard = this.createElement('div', { className: 'pdf-report-card' });
      auditCard.innerHTML = `
        <div class="pdf-report-icon">📋</div>
        <h3>Annual Audit</h3>
        <p>Comprehensive annual report for regulatory compliance</p>
      `;
      const auditSelect = this.createElement('select', { className: 'year-select' }) as HTMLSelectElement;
      const currentYear = new Date().getFullYear();
      for (let year = currentYear; year >= currentYear - 5; year--) {
        const option = this.createElement('option', {
          value: year.toString(),
          textContent: year.toString()
        }) as HTMLOptionElement;
        auditSelect.appendChild(option);
      }
      auditCard.appendChild(auditSelect);
      const auditBtn = this.createElement('button', {
        className: 'btn-primary btn-block',
        textContent: 'Generate PDF'
      });
      auditBtn.addEventListener('click', () => {
        this.downloadAuditPDF(auditSelect.value);
      });
      auditCard.appendChild(auditBtn);
      reportsGrid.appendChild(auditCard);

      section.appendChild(reportsGrid);
    }

    // CSV export: your records leave with you
    const exportRow = this.createElement('div', { className: 'csv-export-row' });
    exportRow.appendChild(this.createElement('h3', { textContent: 'Export your records (CSV)' }));
    exportRow.appendChild(this.createElement('p', { className: 'section-subtitle', textContent: 'Every extinguisher and every inspection for this organization, as spreadsheets. Available on every plan.' }));
    const exportBtns = this.createElement('div', { className: 'csv-export-buttons' });
    const extBtn = this.createElement('button', { className: 'btn-secondary', textContent: 'Download extinguishers.csv' });
    extBtn.addEventListener('click', () => this.downloadCsv('extinguishers'));
    const inspBtn = this.createElement('button', { className: 'btn-secondary', textContent: 'Download inspections.csv' });
    inspBtn.addEventListener('click', () => this.downloadCsv('inspections'));
    exportBtns.appendChild(extBtn);
    exportBtns.appendChild(inspBtn);
    exportRow.appendChild(exportBtns);
    section.appendChild(exportRow);
    return section;
  }

  private async downloadCsv(kind: 'extinguishers' | 'inspections'): Promise<void> {
    try {
      const response = await fetch(`/api/export/${kind}.csv`, { headers: { Authorization: `Bearer ${api.getToken()}` } });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || 'Export failed');
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `fireguard-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      this.showToast('Export downloaded', 'success');
    } catch (error: any) {
      this.showToast(error.message || 'Export failed', 'error');
    }
  }

  /**
   * Only admin and manager hold REPORTS_GENERATE. An inspector can read this
   * page's data but cannot generate a PDF, so offering the button produced a
   * 403 on click with no explanation.
   */
  private canGenerateReports(): boolean {
    const role = (api.currentUser()?.role || '').toLowerCase();
    return role === 'admin' || role === 'manager';
  }

  private async downloadPDFReport(reportType: 'compliance' | 'overdue'): Promise<void> {
    if (!this.canGenerateReports()) {
      this.showToast('Your role can view these figures but not generate the PDF. Ask an admin or manager.', 'error');
      return;
    }
    try {
      this.showToast(`Generating ${reportType} report...`, 'success');

      const url = `/api/reports/${reportType}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${api.getToken()}`
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 404 && reportType === 'overdue') {
          this.showToast('Nothing is overdue right now, so there is no overdue report to generate.', 'success');
          return;
        }
        throw new Error(error.error || 'Failed to generate report');
      }

      // Download the PDF
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${reportType}-report-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      this.showToast('Report downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Error downloading report:', error);
      this.showToast(error.message || 'Failed to download report', 'error');
    }
  }

  private async downloadAuditPDF(year: string): Promise<void> {
    if (!this.canGenerateReports()) {
      this.showToast('Your role can view these figures but not generate the PDF. Ask an admin or manager.', 'error');
      return;
    }
    try {
      this.showToast('Generating annual audit report...', 'success');

      const url = `/api/reports/audit?year=${year}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${api.getToken()}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate audit report');
      }

      // Download the PDF
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `audit-report-${year}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      this.showToast('Audit report downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Error downloading audit report:', error);
      this.showToast(error.message || 'Failed to download audit report', 'error');
    }
  }

  private createExportButton(): HTMLElement {
    const section = this.createElement('div', { className: 'export-section' });

    const exportBtn = this.createElement('button', {
      className: 'btn-secondary btn-large',
      textContent: '📊 Export Report to CSV'
    });

    exportBtn.addEventListener('click', () => this.handleExport());

    section.appendChild(exportBtn);
    return section;
  }

  private async handleExport(): Promise<void> {
    try {
      // Fetch all extinguishers with full details
      const response = await api.get('/api/extinguishers');
      const extinguishers = response.extinguishers || [];

      // Generate CSV
      const headers = [
        'QR Code',
        'Type',
        'Size (lbs)',
        'Location',
        'Compliance Status',
        'Last Inspection',
        'Next Inspection',
        'Days Since Inspection'
      ];

      const rows = extinguishers.map((ext: any) => {
        const lastInspection = ext.last_monthly_inspection
          ? formatDate(ext.last_monthly_inspection)
          : 'Never';

        const nextInspection = ext.next_monthly_inspection
          ? formatDate(ext.next_monthly_inspection)
          : 'Not scheduled';

        const daysSince = ext.last_monthly_inspection
          ? Math.floor((Date.now() - new Date(ext.last_monthly_inspection).getTime()) / (1000 * 60 * 60 * 24))
          : 'N/A';

        return [
          ext.qr_code,
          ext.extinguisher_type,
          ext.size_lbs,
          ext.location?.name || 'Unknown',
          ext.compliance_status,
          lastInspection,
          nextInspection,
          daysSince
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fireguard-report-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.showToast('Report exported successfully!', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      this.showToast('Failed to export report', 'error');
    }
  }

  private handleLogout(): void {
    api.logout();
    router.push('/login');
  }

  private showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success'): void {
    // Routes through the registered <app-toast> component; a bare document.createElement
    // of an unregistered tag renders nothing and silently swallows the message.
    if (type === 'error') Toast.error(message);
    else if (type === 'warning') Toast.warning(message);
    else if (type === 'info') Toast.info(message);
    else Toast.success(message);
  }

  private getStyles(): string {
    return `
      .pdf-empty-note {
        border: 1px dashed var(--gray-300, #d1d5db);
        border-radius: var(--border-radius, 8px);
        padding: 24px;
        text-align: center;
        background: var(--gray-50, #f9fafb);
      }
      .pdf-empty-note h3 { margin: 0 0 8px; font-size: 18px; }
      .pdf-empty-note p { margin: 0 auto 16px; max-width: 52ch; color: var(--gray-500, #6b7280); line-height: 1.5; }

      ${APP_NAV_STYLES}

      .page-container {
        min-height: 100vh;
        background: var(--bg-primary);
      }

      .page-header {
        background: white;
        border-bottom: 1px solid var(--border-color);
        padding: 16px 24px;
        box-shadow: var(--shadow-sm);
      }

      .header-content {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: bold;
        color: var(--primary-color);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .nav-button {
        background: none;
        border: none;
        color: var(--text-primary);
        font-size: 15px;
        cursor: pointer;
        padding: 8px 16px;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .nav-button:hover {
        background: var(--bg-secondary);
      }

      .reports-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: 32px 24px;
      }

      .page-title {
        margin-bottom: 32px;
      }

      .page-title h1 {
        font-size: 32px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .subtitle {
        color: var(--text-secondary);
        font-size: 16px;
        margin: 0;
      }

      .summary-section {
        margin-bottom: 40px;
      }

      .cards-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 24px;
        margin-bottom: 16px;
      }

      .summary-card {
        background: white;
        border-radius: 12px;
        padding: 24px;
        display: flex;
        align-items: flex-start;
        gap: 16px;
        box-shadow: var(--shadow-sm);
        transition: all 0.2s;
      }

      .summary-card:hover {
        box-shadow: var(--shadow-md);
        transform: translateY(-2px);
      }

      .card-icon {
        font-size: 48px;
        line-height: 1;
      }

      .card-content {
        flex: 1;
      }

      .card-title {
        font-size: 14px;
        color: var(--text-secondary);
        margin-bottom: 8px;
      }

      .card-value {
        font-size: 36px;
        font-weight: bold;
        color: var(--text-primary);
        line-height: 1;
      }

      .card-percentage {
        font-size: 14px;
        color: var(--text-secondary);
        margin-top: 4px;
      }

      .card-blue { border-left: 4px solid var(--primary-color); }
      .card-green { border-left: 4px solid var(--success-color); }
      .card-yellow { border-left: 4px solid var(--warning-color); }
      .card-red { border-left: 4px solid var(--danger-color); }

      .last-updated {
        color: var(--text-secondary);
        font-size: 14px;
        text-align: center;
        margin: 0;
      }

      .overdue-section, .recent-section {
        background: white;
        border-radius: 12px;
        padding: 32px;
        margin-bottom: 32px;
        box-shadow: var(--shadow-sm);
      }

      .section-header h2 {
        font-size: 24px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .section-subtitle {
        color: var(--text-secondary);
        font-size: 15px;
        margin: 0 0 24px 0;
      }

      .overdue-table {
        display: flex;
        flex-direction: column;
        gap: 1px;
        background: var(--border-color);
        border-radius: 8px;
        overflow: hidden;
      }

      .table-row {
        display: grid;
        grid-template-columns: 1.5fr 2fr 1.5fr 1.2fr 1.5fr 1fr;
        background: white;
      }

      .table-header {
        background: var(--bg-secondary);
        font-weight: 600;
      }

      .table-cell {
        padding: 16px;
        display: flex;
        align-items: center;
      }

      .days-badge {
        background: var(--danger-color);
        color: white;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 600;
      }

      .btn-small {
        padding: 6px 16px;
        font-size: 14px;
        border-radius: 6px;
        border: none;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s;
      }

      .btn-small.btn-primary {
        background: var(--primary-color);
        color: white;
      }

      .btn-small.btn-primary:hover {
        background: var(--primary-hover);
      }

      .inspections-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .inspection-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        background: var(--bg-secondary);
        border-radius: 8px;
        transition: all 0.2s;
      }

      .inspection-item:hover {
        background: var(--border-color);
      }

      .inspection-icon {
        font-size: 32px;
        line-height: 1;
      }

      .inspection-content {
        flex: 1;
      }

      .inspection-title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .inspection-meta {
        font-size: 14px;
        color: var(--text-secondary);
      }

      .inspection-status {
        padding: 6px 16px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
      }

      .status-pass {
        background: #d1fae5;
        color: #065f46;
      }

      .status-warning {
        background: #fef3c7;
        color: #92400e;
      }

      .status-fail {
        background: #fee2e2;
        color: #991b1b;
      }

      .empty-message {
        text-align: center;
        color: var(--text-secondary);
        padding: 40px;
        font-style: italic;
      }

      .csv-export-row { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--gray-200); }
      .csv-export-row h3 { font-size: 18px; margin-bottom: 4px; }
      .csv-export-buttons { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
      .pdf-reports-section {
        background: white;
        border-radius: 12px;
        padding: 32px;
        margin-bottom: 32px;
        box-shadow: var(--shadow-sm);
      }

      .pdf-reports-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 24px;
        margin-top: 24px;
      }

      .pdf-report-card {
        background: var(--bg-secondary);
        border-radius: 12px;
        padding: 24px;
        text-align: center;
        transition: all 0.2s;
      }

      .pdf-report-card:hover {
        box-shadow: var(--shadow-md);
        transform: translateY(-4px);
      }

      .pdf-report-icon {
        font-size: 48px;
        margin-bottom: 16px;
      }

      .pdf-report-card h3 {
        font-size: 18px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .pdf-report-card p {
        font-size: 14px;
        color: var(--text-secondary);
        margin-bottom: 16px;
      }

      .year-select {
        width: 100%;
        padding: 12px;
        border: 1px solid var(--border-color);
        border-radius: 6px;
        font-size: 14px;
        margin-bottom: 12px;
        background: white;
        cursor: pointer;
      }

      .btn-block {
        width: 100%;
      }

      .export-section {
        text-align: center;
        margin-top: 40px;
      }

      .btn-large {
        padding: 16px 32px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary {
        background: var(--text-primary);
        color: white;
      }

      .btn-secondary:hover {
        background: #374151;
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      @media (max-width: 1200px) {
        .table-row {
          grid-template-columns: 1fr 1.5fr 1fr 1fr 1fr 0.8fr;
          font-size: 14px;
        }

        .table-cell {
          padding: 12px;
        }
      }

      @media (max-width: 768px) {
        .cards-grid {
          grid-template-columns: 1fr;
        }

        .table-row {
          grid-template-columns: 1fr;
        }

        .table-cell {
          border-bottom: 1px solid var(--border-color);
        }

        .table-cell:last-child {
          border-bottom: none;
        }
      }
    `;
  }
}

customElements.define('reports-page', ReportsPage);
